import React from 'react';
import { motion } from 'framer-motion';
import {
    Zap,
    Shield,
    BarChart3,
    Globe,
    Clock,
    Users,
    MessageSquare,
    TrendingUp,
    Smartphone,
    CheckCircle,
    DollarSign,
    Headphones,
    Send,
    Server,
    Lock
} from 'lucide-react';
import features from "../assets/features.webp";

// Animation Variants for the container (Word)
const wordVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1,
        },
    },
};

// Animation Variants for each letter
const letterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            damping: 12,
            stiffness: 100,
        },
    },
};

// Helper component to split text into letters
const AnimatedText = ({ text, className, style }) => {
    const letters = text.split("");
    return (
        <motion.span
            className={`inline-block ${className}`}
            style={style}
            variants={wordVariants}
            initial="hidden"
            animate="visible"
        >
            {letters.map((letter, index) => (
                <motion.span key={index} variants={letterVariants}>
                    {letter}
                </motion.span>
            ))}
        </motion.span>
    );
};

// Features data
const featureItems = [
    {
        icon: Send,
        title: " Super Fast SMS Delivery",
        description: "Your SMS reaches customers instantly through our optimized, high-speed delivery channels",
        stats: "≤ 500ms",
        color: "from-blue-500 to-cyan-400",
        bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
        delay: 0.1
    },
    {
        icon: Shield,
        title: "Bank-Grade Security",
        description: "End-to-end encryption, GDPR compliant, ISO 27001 certified",
        stats: "100% Secure",
        color: "from-emerald-500 to-green-400",
        bgColor: "bg-gradient-to-br from-emerald-50 to-green-50",
        delay: 0.2
    },
    {
        icon: BarChart3,
        title: "Real-Time Analytics",
        description: "Live tracking, delivery reports, click-through rates & conversion analytics",
        stats: "24/7 Monitoring",
        color: "from-purple-500 to-pink-400",
        bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
        delay: 0.3
    },
    {
        icon: Globe,
        title: "Honest & Trustworthy",
        description: "In just 2 to 5 seconds, your OTP SMS reaches customers instantly, powered by the high-speed Eurofox SMS Service infrastructure",
        stats: "Verified",
        color: "from-orange-500 to-amber-400",
        bgColor: "bg-gradient-to-br from-orange-50 to-amber-50",
        delay: 0.4
    },
    {
        icon: Clock,
        title: "Scheduled Messaging",
        description: "Plan campaigns in advance with timezone-aware scheduling",
        stats: "Any Time Zone",
        color: "from-red-500 to-rose-400",
        bgColor: "bg-gradient-to-br from-red-50 to-rose-50",
        delay: 0.5
    },
    {
        icon: Users,
        title: "Team Collaboration",
        description: "Multi-user accounts with role-based permissions and approval workflows",
        stats: "Unlimited Users",
        color: "from-indigo-500 to-violet-400",
        bgColor: "bg-gradient-to-br from-indigo-50 to-violet-50",
        delay: 0.6
    },
    {
        icon: MessageSquare,
        title: "Two-Way Messaging",
        description: "Enable customer replies with automated responses and AI routing",
        stats: "2-Way SMS",
        color: "from-teal-500 to-emerald-400",
        bgColor: "bg-gradient-to-br from-teal-50 to-emerald-50",
        delay: 0.7
    },
    {
        icon: Server,
        title: "Scalable Infrastructure",
        description: "Handle millions of messages simultaneously with auto-scaling",
        stats: "10M+/Hour",
        color: "from-amber-500 to-yellow-400",
        bgColor: "bg-gradient-to-br from-amber-50 to-yellow-50",
        delay: 0.8
    },
    {
        icon: Lock,
        title: "Compliance Ready",
        description: "Built-in DNC management, opt-out handling, and regulatory compliance",
        stats: "TCPA Compliant",
        color: "from-sky-500 to-blue-400",
        bgColor: "bg-gradient-to-br from-sky-50 to-blue-50",
        delay: 0.9
    }
];

const Features = () => {
    return (
        <div className="bg-gradient-to-br from-white via-slate-50 to-blue-100 min-h-screen font-sans text-slate-900 overflow-x-hidden">

            {/* Hero Section - Unchanged */}
            <section className="relative pt-10 pb-16 lg:pt-16 lg:pb-24"> {/* Reduced pb-24 to pb-16, lg:pb-32 to lg:pb-24 */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

                        {/* LEFT SIDE: Big 3D Typography */}
                        <div className="relative z-10">
                            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight text-slate-900 mb-8">
                                <div className="mb-4">
                                    <AnimatedText text="REACH" />
                                </div>
                                <div className="mb-4">
                                    <AnimatedText text="EVERY" />
                                </div>
                                <div>
                                    <AnimatedText
                                        text="CUSTOMER."
                                        className="text-[#F46653] relative"
                                        style={{
                                            textShadow: "4px 4px 0px #0C4550",
                                            WebkitTextStroke: "1px #0C4550"
                                        }}
                                    />
                                </div>
                            </h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.2, duration: 0.6 }}
                                className="text-xl text-slate-600 mb-10 max-w-lg font-medium leading-relaxed"
                            >
                                Connect instantly with millions. The most reliable bulk SMS platform designed for high-throughput and maximum conversion.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.4, duration: 0.6 }}
                                className="flex flex-col sm:flex-row gap-4"
                            >
                                <button className="px-10 py-5 rounded-xl bg-[#0C4550] text-white font-bold text-lg hover:bg-[#09353e] hover:-translate-y-1 transition-all duration-300">
                                    Start Sending Now
                                </button>
                                <button className="px-10 py-5 rounded-xl border-2 border-slate-200 text-slate-700 font-bold text-lg hover:border-[#0C4550] hover:text-[#0C4550] hover:bg-slate-50 transition-all duration-300">
                                    View Features
                                </button>
                            </motion.div>
                        </div>

                        {/* RIGHT SIDE: Image Placeholder */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, rotate: 0 }}
                            animate={{ opacity: 1, scale: 1, rotate: 2 }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                            className="relative"
                        >
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-indigo-100 to-blue-50 rounded-full blur-3xl -z-10 opacity-60"></div>
                            <div className="relative w-full aspect-square md:aspect-[4/3] bg-slate-50 rounded-3xl shadow-2xl overflow-hidden transform">
                                <img
                                    src={features}
                                    alt="SMS Gateway Dashboard mockup"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Features Section with Updated Header - Reduced top padding */}
            <section className="pt-12 pb-20 lg:pt-16 lg:pb-32 bg-[#F0EFEB]"> {/* Reduced py-20 to pt-12 pb-20, lg:py-32 to lg:pt-16 lg:pb-32 */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header - Updated with reduced size and 2 lines */}
                    <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"> {/* Reduced mb-20 to mb-12 lg:mb-16 */}
                        <motion.span
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            className="text-[#0C4550] font-bold tracking-wider uppercase text-medium mb-2 block"
                        >
                            Why Choose Eurofox
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight text-slate-900 mb-6"
                        >
                            {/* First line with animated text - "POWERFUL FEATURES" */}
                            <div className="mb-2">
                                <AnimatedText text="POWERFUL FEATURES" />
                            </div>

                            {/* Second line with gradient and 3D effect - "FOR GROWTH." */}
                            <div>
                                <AnimatedText
                                    text="FOR GROWTH."
                                    className="text-[#F46653] relative"
                                    style={{
                                        textShadow: "4px 4px 0px #0C4550", WebkitTextStroke: "1px #0C4550"
                                    }}
                                />
                            </div>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="text-xl text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed"
                        >
                            Everything you need to launch, manage, and scale your SMS marketing campaigns with enterprise-grade reliability.
                        </motion.p>
                    </div>

                    {/* Features Grid - 3x3 layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7"> {/* Reduced gap-6 to gap-5, lg:gap-8 to lg:gap-7 */}
                        {featureItems.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: feature.delay }}
                                    whileHover={{
                                        y: -8,
                                        transition: { duration: 0.3 }
                                    }}
                                    className="relative group"
                                >
                                    {/* Premium Card Container */}
                                    <div className={`relative h-full rounded-2xl ${feature.bgColor} border border-white/50 backdrop-blur-sm overflow-hidden`}>
                                        {/* Glass morphism effect */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />

                                        {/* Animated gradient border */}
                                        <div className="absolute inset-0 rounded-2xl p-px bg-gradient-to-br from-white/80 via-transparent to-transparent">
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 from-white/50 via-transparent to-transparent" />
                                        </div>

                                        {/* Content */}
                                        <div className="relative p-6 lg:p-7 h-full flex flex-col"> {/* Reduced lg:p-8 to lg:p-7 */}
                                            {/* Icon with gradient background */}
                                            <div className="mb-5 relative"> {/* Reduced mb-6 to mb-5 */}
                                                <div className={`absolute inset-0 w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} opacity-10 blur-md`} />
                                                <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center`}>
                                                    <Icon className="w-7 h-7 text-white" />
                                                </div>
                                            </div>

                                            {/* Title & Description */}
                                            <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-3"> {/* Reduced text-2xl to text-xl lg:text-2xl */}
                                                {feature.title}
                                            </h3>
                                            <p className="text-slate-600 mb-5 flex-grow text-sm lg:text-base"> {/* Reduced mb-6 to mb-5, added text-sm lg:text-base */}
                                                {feature.description}
                                            </p>

                                            {/* Stats Badge */}
                                            <div className="mt-auto">
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 backdrop-blur-sm border border-white/40"> {/* Reduced px-4 py-2 to px-3 py-1.5 */}
                                                    <span className="text-xs font-bold text-slate-800"> {/* Reduced text-sm to text-xs */}
                                                        {feature.stats}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Hover effect line */}
                                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-slate-900/20 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                                        </div>
                                    </div>

                                    {/* Floating particles effect on hover */}
                                    <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute -bottom-2 -left-2 w-3 h-3 rounded-full bg-gradient-to-br from-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150" />
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Enterprise Features Banner - Adjusted margin top */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-16 lg:mt-24" /* Reduced mt-20 to mt-16, lg:mt-32 to lg:mt-24 */
                    >
                        <div className="relative rounded-3xl overflow-hidden">
                            {/* Background gradient */}
                            <div className="absolute inset-0 bg-gradient-to-r from-[#0C4550] via-[#0C4550]/90 to-[#F46653]/20" />

                            {/* Pattern overlay */}
                            <div className="absolute inset-0 opacity-5"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                                }}
                            />

                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Features;


